// packages/lsp/src/io-vfs.ts
//
// In-memory LspIO backed by a Map<path, content>. Used by the browser
// worker entry and by Studio. URIs use the `memfs://` scheme so they
// never collide with on-disk file:// URIs.

import type { DirEntry, LspIO } from "./io.js";

const SCHEME = "memfs://";

/**
 * Translate a URI to a VFS path. Accepts memfs:// (canonical), plus any
 * other scheme by extracting the path portion — Monaco standalone uses
 * `inmemory://model/N` for unnamed editor buffers, and the LSP client
 * passes those through to the server during workspace bootstrap. We
 * normalize them all to a single path namespace; callers that pass a
 * scheme we have no files for get a graceful "not found" rather than a
 * thrown exception.
 */
function uriToPath(uri: string): string {
  const schemeMatch = uri.match(/^[a-z][a-z0-9+.-]*:\/+(.*)$/i);
  const body = schemeMatch ? schemeMatch[1]! : uri.replace(/^\/+/, "");
  // Through the SAME normalizer the keys use. Trimming slashes here while keys
  // resolved "."/".." made the two entry points contradict each other: a URI
  // carrying ".." read back null for a file `joinUri` resolves to correctly.
  return normKey(body);
}

function pathToUri(p: string): string {
  const norm = p.startsWith("/") ? p : "/" + p;
  return SCHEME + norm;
}

/** Canonical form of a VFS KEY — "svc//api/./m.arch" → "/svc/api/m.arch".
 *
 *  Only the URI side was normalized before. A key carrying an empty or "."
 *  segment therefore hid its ENTIRE subtree (`getDirEntries` drops an empty
 *  name), so a package under it was invisible here while the web twin — which
 *  routes every path through `vfsJoin` — found it. The two are required to
 *  agree on what a package is. */
export function normKey(p: string): string {
  if (isCanonical(p)) return p; // the overwhelmingly common case, allocation-free
  const segs: string[] = [];
  for (const part of p.split("/")) {
    if (part === "" || part === ".") continue;
    if (part === "..") segs.pop();
    else segs.push(part);
  }
  return "/" + segs.join("/");
}

/** Is this key already in canonical form? Leading "/", no empty segment, no
 *  "." or ".." segment. Lets `normKey` skip the split/join allocation. */
function isCanonical(p: string): boolean {
  return p === "/" || (p.startsWith("/") && !/\/\/|\/\.{1,2}(?=\/|$)|\/$/.test(p));
}

/**
 * Write a batch of files into a VFS map, canonicalizing keys to the
 * "/"-prefixed form every ioVfs READ uses. This is THE write boundary for
 * `arch/setWorkspace` — a raw `vfs.set(path, …)` with a bare key (Studio
 * stores key files repo-relative) produces entries readFile/readDirectory
 * can never see, i.e. a worker with an invisible user workspace.
 */
export function setVfsFiles(
  vfs: Map<string, string>,
  files: Record<string, string>,
  replace: boolean | undefined,
): void {
  if (replace) vfs.clear();
  for (const [path, content] of Object.entries(files)) {
    const key = normKey(path);
    // "/dir/.." normalizes to "/" — the VFS ROOT, which is a directory. Writing
    // a file there made `isDirectory("memfs:///")` false and shadowed the whole
    // tree. Such a key names no file; drop it rather than corrupt the namespace.
    if (key === "/") continue;
    vfs.set(key, content);
  }
}

export interface IoVfsOptions {
  /**
   * Root URI for the bundled stdlib inside this VFS. The host writes
   * stdlib files under this prefix via `arch/setWorkspace`; the
   * package-loader reads them through `LspIO.defaultStdlibUri()`.
   * Defaults to `memfs:///__stdlib__`.
   */
  readonly stdlibUri?: string;
}

export function ioVfs(
  vfs: Map<string, string>,
  opts: IoVfsOptions = {},
): LspIO {
  const stdlibUri = opts.stdlibUri ?? SCHEME + "/__stdlib__";

  // Reads normalize keys defensively — `setVfsFiles` is the documented write
  // boundary, but a raw `vfs.set` (Studio keys its working set repo-relative,
  // i.e. bare) reaches the same map, and such a key is invisible to every read:
  // a workspace the worker cannot see.
  //
  // That defence must not be memoized. Caching "this map holds no raw key" on
  // `vfs.size` looked safe — a `vfs.set` of an unseen key grows the map — but a
  // delete paired with a set in the same tick leaves the size unchanged, and
  // the stale `false` turned the fallback into an early `return null`. The
  // guard failed in exactly the raw-`vfs.set` case it exists for.
  //
  // So the scan stays unconditional and is made CHEAP instead: `isCanonical` is
  // one regex test per key, with no split/join allocation. That allocation was
  // the actual cost — ~1ms per miss on a real workspace, on a hot path
  // (`loadProjectConfigFromUri` walks up the tree probing for `archlang.json`,
  // and every level is a miss).

  function getDirEntries(dirPath: string): DirEntry[] {
    const prefix = dirPath === "/" ? "/" : dirPath + "/";
    const seen = new Set<string>();
    const out: DirEntry[] = [];
    for (const rawKey of vfs.keys()) {
      const key = normKey(rawKey);
      if (!key.startsWith(prefix)) continue;
      const rest = key.slice(prefix.length);
      const slash = rest.indexOf("/");
      const name = slash === -1 ? rest : rest.slice(0, slash);
      if (!name || seen.has(name)) continue;
      seen.add(name);
      out.push({ name, kind: slash === -1 ? "file" : "dir" });
    }
    return out;
  }

  return {
    async readFile(uri) {
      const p = uriToPath(uri);
      if (vfs.has(p)) return vfs.get(p)!;
      // Miss → the key may simply be spelled differently (bare, "//", "/./").
      for (const [key, text] of vfs) if (isCanonical(key) ? key === p : normKey(key) === p) return text;
      return null;
    },
    async readDirectory(uri) {
      return getDirEntries(uriToPath(uri));
    },
    async exists(uri) {
      const p = uriToPath(uri);
      if (vfs.has(p)) return true;
      // Directory exists if any key starts with `p + "/"` or matches root.
      if (p === "/") return true;
      const prefix = p + "/";
      // A raw key must be matched as a FILE too, not only as a directory
      // prefix: `readFile` had its normalized-equality fallback while this one
      // tested prefixes only, so a bare key named a file readFile could read
      // and `exists` denied — and `loadPackage` throws "Package directory not
      // found" before it ever gets to the read.
      for (const key of vfs.keys()) {
        const k = isCanonical(key) ? key : normKey(key);
        if (k === p || k.startsWith(prefix)) return true;
      }
      return false;
    },
    async isDirectory(uri) {
      const p = uriToPath(uri);
      if (vfs.has(p)) return false;
      if (p === "/") return true;
      const prefix = p + "/";
      for (const key of vfs.keys()) {
        // Only "holds children" makes a directory; a raw key EQUAL to `p` names
        // a file, which is the `false` this returns by default.
        if ((isCanonical(key) ? key : normKey(key)).startsWith(prefix)) return true;
      }
      return false;
    },
    joinUri(base, ...segments) {
      const basePath = uriToPath(base);
      // Resolve `.`/`..` segments so relative dependency paths work — e.g. a
      // stdlib manifest's `dependencies { arch.ui: "../arch.ui" }` joins to a
      // sibling dir. The node FS io + viewer-core's VFS both resolve `..`; the
      // browser VFS must too, or `../x` stays literal and the dir isn't found.
      const segs = basePath === "/" ? [] : basePath.split("/").filter(Boolean);
      for (const part of segments.flatMap((s) => s.split("/"))) {
        if (part === "" || part === ".") continue;
        if (part === "..") segs.pop();
        else segs.push(part);
      }
      return pathToUri("/" + segs.join("/"));
    },
    dirname(uri) {
      const p = uriToPath(uri);
      const slash = p.lastIndexOf("/");
      if (slash <= 0) return pathToUri("/");
      return pathToUri(p.slice(0, slash));
    },
    async defaultStdlibUri() {
      return stdlibUri;
    },
  };
}
